import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import { UsersThree, MapPin, Plus, ChatCircleDots, CircleNotch } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const EXAMPLES = ["Kerala Retired Mathematics Teachers", "India Physics Teachers Network", "Former Kendriya Vidyalaya Principals", "Indian Teachers in the United States"];

export default function FacultyNetworks() {
  useSeo("Faculty Networks", "Cross-institution communities for teachers & staff — connected by subject, region and role.");
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    api.get(`/public/faculty-networks?${p.toString()}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  };
  useEffect(() => { const t = setTimeout(load, rows === null ? 0 : 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="mx-auto max-w-5xl px-5 py-12" data-testid="faculty-networks-page">
      <p className="overline text-secondary mb-2">Teachers • Staff • Mentors</p>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-light tracking-tight">Faculty Networks</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Wider communities that span institutions — by subject, region or role. Start or join a network of educators.</p>
        </div>
        {user && <CreateNetworkDialog onCreated={load} />}
      </div>

      <div className="mt-6 max-w-md">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search networks…" className="rounded-full" data-testid="network-search" />
      </div>

      {rows === null ? <p className="mt-10 text-center text-muted-foreground">Loading…</p>
        : rows.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-dashed border-border/60 p-10 text-center" data-testid="networks-empty">
            <UsersThree size={34} weight="duotone" className="mx-auto mb-3 text-secondary" />
            <p className="text-sm text-muted-foreground">No networks yet. Be the first to start one — for example:</p>
            <div className="mt-3 flex flex-wrap justify-center gap-2">
              {EXAMPLES.map((e) => <span key={e} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">{e}</span>)}
            </div>
          </div>
        ) : (
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((g) => (
              <Link key={g.id} to={`/groups/${g.id}`} data-testid={`network-card-${g.id}`}
                className="group rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-secondary hover:shadow-md">
                <div className="flex items-center gap-2"><UsersThree size={20} weight="duotone" className="text-secondary" /><h3 className="font-heading text-lg font-medium group-hover:text-primary">{g.name}</h3></div>
                {g.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{g.description}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  {[g.district, g.region, g.country].filter(Boolean).length > 0 && <span className="flex items-center gap-1"><MapPin size={12} /> {[g.district, g.region, g.country].filter(Boolean).join(", ")}</span>}
                  <span className="flex items-center gap-1"><ChatCircleDots size={12} /> {g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
    </div>
  );
}

function CreateNetworkDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", description: "", country: "", region: "", district: "", privacy: "public" });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { toast.error("Give the network a name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { ...f, name: f.name.trim(), type: "faculty", faculty: true, scope: "network" });
      toast.success("Network created");
      setOpen(false); setF({ name: "", description: "", country: "", region: "", district: "", privacy: "public" });
      if (onCreated) onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full gap-1.5" data-testid="create-network-open"><Plus size={16} /> Start a network</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="create-network-dialog">
        <DialogHeader><DialogTitle>Start a Faculty Network</DialogTitle><DialogDescription>A cross-institution community for teachers &amp; staff — connect by subject, region or role.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Network name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" placeholder="Kerala Retired Mathematics Teachers" data-testid="network-name" /></div>
          <div><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1" data-testid="network-desc" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Country</Label><Input value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="mt-1" placeholder="India" data-testid="network-country" /></div>
            <div><Label className="text-xs">State</Label><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} className="mt-1" placeholder="Kerala" data-testid="network-region" /></div>
            <div><Label className="text-xs">District</Label><Input value={f.district} onChange={(e) => setF({ ...f, district: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} className="rounded-full gap-1.5" data-testid="network-submit">{busy ? <CircleNotch size={15} className="animate-spin" /> : <Plus size={15} />} {busy ? "Creating…" : "Create network"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
